import { useCallback, useEffect, useRef } from 'react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { useLazyQuery, useMutation, useApolloClient } from '@apollo/client/react';
import { getExpertoBannerPreference } from '../graphql/queries/getExpertoBannerPreference';
import { updateExpertoBannerPreference } from '../graphql/mutations/updateExpertoBannerPreference';
import { createExpertoBannerPreference } from '../graphql/mutations/createExpertoBannerPreference';
import { getCookie } from '../utils/cookies';

const EXPERTO_MIGRATION_COOKIE = 'experto-field-migration-banner';

interface ExpertoBannerPreference {
  id: string;
  showBanner: boolean;
}

interface GetExpertoBannerPreferenceData {
  expertoBannerPreferences?: ExpertoBannerPreference;
}

interface GetExpertoBannerPreferenceVars {
  userId: string;
}

interface UpdateExpertoBannerPreferenceData {
  updateExpertoBannerPreference?: ExpertoBannerPreference;
}

interface UpdateExpertoBannerPreferenceVars {
  userId: string;
  preferenceId: string;
  showBanner: boolean;
}

interface CreateExpertoBannerPreferenceData {
  createExpertoBannerPreference?: ExpertoBannerPreference;
}

interface CreateExpertoBannerPreferenceVars {
  userId: string;
  showBanner: boolean;
}

const hasInternalServerError = (error: unknown): boolean =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some((graphQLError) => graphQLError.extensions?.code === 'INTERNAL_SERVER_ERROR');

export const useExpertoBannerPreference = () => {
  const client = useApolloClient();
  const hasSyncedCookiePreferenceRef = useRef(false);
  const lastFetchedUserIdRef = useRef<string | undefined>(undefined);
  const hasCompletedExpertoMigration = getCookie(EXPERTO_MIGRATION_COOKIE) !== null;

  const [fetchPreference, { data: preferenceData }] =
    useLazyQuery<GetExpertoBannerPreferenceData, GetExpertoBannerPreferenceVars>(
      getExpertoBannerPreference,
      { client }
    );

  const [createPreference] =
    useMutation<CreateExpertoBannerPreferenceData, CreateExpertoBannerPreferenceVars>(
      createExpertoBannerPreference,
      { client }
    );

  const [updatePreference] =
    useMutation<UpdateExpertoBannerPreferenceData, UpdateExpertoBannerPreferenceVars>(
      updateExpertoBannerPreference,
      { client }
    );

  const fetchPreferenceWithFallback = useCallback(async (...args: Parameters<typeof fetchPreference>): Promise<void> => {
    lastFetchedUserIdRef.current = args[0]?.variables?.userId;
    const userId = args[0]?.variables?.userId;

    try {
      const result = await fetchPreference(...args);

      if (result.error) {
        throw result.error;
      }
    } catch (error) {
      if (hasInternalServerError(error)) {
        if (userId) {
          await createPreference({
            variables: {
              userId,
              showBanner: !hasCompletedExpertoMigration,
            },
          });

          await fetchPreference(...args);
        }
      }

      throw error;
    }
  }, [createPreference, fetchPreference, hasCompletedExpertoMigration]);

  useEffect(() => {
    if (!hasCompletedExpertoMigration) {
      hasSyncedCookiePreferenceRef.current = false;
      return;
    }

    const preferenceId = preferenceData?.expertoBannerPreferences?.id;
    const shouldSyncPreference = preferenceData?.expertoBannerPreferences?.showBanner === true;
    const userId = lastFetchedUserIdRef.current;

    if (!shouldSyncPreference || !preferenceId || !userId || hasSyncedCookiePreferenceRef.current) {
      return;
    }

    hasSyncedCookiePreferenceRef.current = true;

    updatePreference({
      variables: {
        userId,
        preferenceId,
        showBanner: false,
      },
    }).catch(() => {
      hasSyncedCookiePreferenceRef.current = false;
    });
  }, [hasCompletedExpertoMigration, preferenceData, updatePreference]);

  return {
    fetchPreference: fetchPreferenceWithFallback,
    showBanner: !hasCompletedExpertoMigration
      && preferenceData?.expertoBannerPreferences?.showBanner === true,
    preferenceId: preferenceData?.expertoBannerPreferences?.id,
    updatePreference,
  };
};
